/**
 * Custom testimonial layered rotator — no off-the-shelf lib does this.
 *
 *   Selector:  `.carousel-card` (3+ siblings inside a `.carousel-track`)
 *   Behaviour: cycles `.active` / `.middle` / `.back` modifier classes so the
 *              vendor CSS can transform the cards into a layered stack.
 *              Click any card OR swipe horizontally to advance.
 *
 * No-op when fewer than 2 cards are present — safe to call against every
 * shadow root.
 */

export function initTestimonialRotator(root: ShadowRoot | Document): void {
  const cards = Array.from(root.querySelectorAll('.carousel-card')) as HTMLElement[]
  if (cards.length < 2) return

  // Container that owns the pointer/touch gestures. `.carousel-container` is
  // the legacy markup's wrapper; fall back to the cards' parent if absent.
  const container =
    (cards[0].closest('.carousel-container') as HTMLElement | null) ?? cards[0].parentElement
  if (!container) return

  const total = cards.length
  let currentIndex = 0
  // Set by the pointer-move handler when the gesture clears the click threshold.
  // Click handlers consult it so a swipe-release doesn't also trigger a tap.
  let suppressClicks = false

  const applyState = () => {
    cards.forEach((card, i) => {
      card.classList.remove('active', 'middle', 'back')
      const rel = (i - currentIndex + total) % total
      if (rel === 0) card.classList.add('active')
      else if (rel === 1) card.classList.add('middle')
      else card.classList.add('back')
    })
  }

  const goTo = (next: number) => {
    currentIndex = ((next % total) + total) % total
    applyState()
  }

  cards.forEach((card, i) => {
    card.addEventListener('click', event => {
      if (suppressClicks) return
      // Don't steal clicks from CTAs nested inside a testimonial card.
      if ((event.target as Element)?.closest('[data-connect-id], a, button')) return
      if (i === currentIndex) return
      event.preventDefault()
      goTo(i)
    })
  })

  // Pointer events cover mouse + touch + pen in a single code path. The legacy
  // CSS already animates `.active/.middle/.back` transitions for 0.6s, so we
  // don't need to mirror the drag delta onto the card transforms — releasing
  // past the threshold just triggers the same class swap a click would.
  const SWIPE_THRESHOLD_PX = 40
  const CLICK_TO_DRAG_PX = 5

  let dragStartX = 0
  let dragDeltaX = 0
  let dragPointerId: number | null = null

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    dragPointerId = e.pointerId
    dragStartX = e.clientX
    dragDeltaX = 0
    suppressClicks = false
  }

  const onPointerMove = (e: PointerEvent) => {
    if (dragPointerId !== e.pointerId) return
    dragDeltaX = e.clientX - dragStartX
    if (!suppressClicks && Math.abs(dragDeltaX) > CLICK_TO_DRAG_PX) {
      suppressClicks = true
      // Capture only once the gesture is a drag. Capturing on pointerdown
      // retargets the follow-up `click` to the container, so a plain tap
      // would never reach the card's click handler.
      container.setPointerCapture?.(e.pointerId)
    }
  }

  const onPointerEnd = (e: PointerEvent) => {
    if (dragPointerId !== e.pointerId) return
    if (container.hasPointerCapture?.(e.pointerId)) container.releasePointerCapture(e.pointerId)
    dragPointerId = null
    // Negative delta = finger moved left = bring the next card forward.
    // Positive delta = finger moved right = bring the previous card forward.
    if (dragDeltaX <= -SWIPE_THRESHOLD_PX) goTo(currentIndex + 1)
    else if (dragDeltaX >= SWIPE_THRESHOLD_PX) goTo(currentIndex - 1)
    // Reset on the next tick so the synthetic `click` that fires after the
    // pointer sequence still sees suppressClicks=true.
    setTimeout(() => { suppressClicks = false }, 0)
  }

  container.addEventListener('pointerdown', onPointerDown)
  container.addEventListener('pointermove', onPointerMove)
  container.addEventListener('pointerup', onPointerEnd)
  container.addEventListener('pointercancel', onPointerEnd)
  // Reserve the horizontal axis for the carousel; let the browser keep
  // vertical page scroll. Without this, mobile Safari treats a horizontal
  // pointer-move as ambient scroll and steals the gesture mid-drag.
  container.style.touchAction = 'pan-y'

  applyState()
}
